<template>
	<div class="date-filter-container" :class="{ active: selectedOption }">
		<Dropdown
			v-model="selectedOption"
			global
			pill
			small
			showOverlay
			:options="options"
			:optionsClass="`date-filter-options`"
			:placeholder="t('search_date_filter_title')"
			@open="dropdownOpen"
			@change="onChange"
		>
			<template v-slot="{ option }">
				<div
					:class="{
						'selected-option':
							selectedOption === option.id ||
							(!selectedOption && option.id === 'ANY'),
					}"
				>
					<span>{{ option.label }}</span>
					<CheckIcon
						class="checkmark-icon"
						type="solid"
						v-if="
							selectedOption === option.id ||
							(!selectedOption && option.id === 'ANY')
						"
					/>
				</div>
			</template>
		</Dropdown>

		<DatePickerModal
			v-if="showDatePicker"
			v-model="filterDate"
			:confirmText="t('search_date_filter_setdate')"
			:cancelText="t('common_button_cancel')"
			noFutureDates
			:isRange="isRange"
			@close="onClose"
		/>
	</div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Dropdown, DatePickerModal } from '@/components/common'
import { CheckIcon } from '@/icons'
import { useI18n } from 'vue-i18n'
import moment from 'moment'

const { t } = useI18n()

const props = defineProps({
	startDate: { type: String, default: null },
	endDate: { type: String, default: null },
	relativeDate: { type: String, default: null },
})
const emit = defineEmits(['update', 'open'])
const selectedOption = ref(null)
const datePickerMode = ref('')
const isDropdownOpen = ref(false)
const datePickerOptions = ['ON', 'BEFORE', 'AFTER', 'RANGE']
const options = ref([])

const showDatePicker = ref(false)
const filterDate = ref(null)

watch(filterDate, (date) => {
	if (!date) return
	updateOptions()
	emitUpdate()
})

const isRange = computed(() => {
	return (
		datePickerMode.value === 'RANGE' ||
		(!!props.startDate &&
			!!props.endDate &&
			!isSameDay.value &&
			!datePickerMode.value)
	)
})

const isSameDay = computed(() => {
	return (
		datePickerMode.value === 'ON' ||
		moment(props.startDate).isSame(moment(props.endDate), 'day')
	)
})

const isAfter = computed(() => {
	return (
		datePickerMode.value === 'AFTER' || (!props.endDate && !!props.startDate)
	)
})

const isBefore = computed(() => {
	return (
		datePickerMode.value === 'BEFORE' || (!props.startDate && !!props.endDate)
	)
})

const isOpen = computed(() => {
	return isDropdownOpen.value || showDatePicker.value
})

watch(
	() => props.relativeDate,
	(date) => {
		determineState()
	}
)

watch(
	() => props.startDate,
	(date) => {
		determineState()
	}
)

watch(
	() => props.endDate,
	(date) => {
		determineState()
	}
)

watch(
	() => isOpen.value,
	(open) => {
		emit('open', open)
	}
)

function onChange(option) {
	resetOptions()
	filterDate.value = null
	if (datePickerOptions.includes(option)) {
		showDatePicker.value = true
		datePickerMode.value = option
	} else {
		if (option === 'ANY') {
			selectedOption.value = null
		}
		datePickerMode.value = ''
		emitUpdate()
	}
}

function onClose() {
	showDatePicker.value = false
	if (!filterDate.value) {
		datePickerMode.value = ''
		selectedOption.value = null
	}
}

function resetOptions() {
	options.value = [
		{ id: 'ANY', label: t('search_date_filter_anytime') },
		{ id: 'P7D', label: t('search_date_filter_last7days') },
		{ id: 'P30D', label: t('search_date_filter_last30days') },
		{ id: 'P3M', label: t('search_date_filter_last3months') },
		{ id: 'P1Y', label: t('search_date_filter_last12months') },
		{ id: 'ON', label: t('search_date_filter_on') },
		{ id: 'BEFORE', label: t('search_date_filter_before') },
		{ id: 'AFTER', label: t('search_date_filter_after') },
		{ id: 'RANGE', label: t('search_date_filter_range') },
	]
}

function updateOptions() {
	if (!filterDate.value) return
	if (!isRange.value) {
		const formattedDate = moment(filterDate.value).format('MMM D, YYYY')
		const onOptionIndex = options.value.findIndex(
			(option) => option.id === datePickerMode.value
		)
		if (onOptionIndex !== -1) {
			const selected = options.value[onOptionIndex]
			// selected.label = `${t(`search_date_filter_${selected.id.toLowerCase()}`)}: ${formattedDate}`
			selected.label = t(
				`search_date_filter_${selected.id.toLowerCase()}_with_value`,
				{ Date: formattedDate }
			)
		}
	} else {
		const { start, end } = filterDate.value
		const formattedStart = moment(start).format('M/D/YY')
		const formattedEnd = moment(end).format('M/D/YY')

		const onOptionIndex = options.value.findIndex(
			(option) => option.id === datePickerMode.value
		)
		if (onOptionIndex !== -1) {
			const selected = options.value[onOptionIndex]
			selected.label = t('search_date_filter_range_with_values', {
				Start: formattedStart,
				End: formattedEnd,
			})
		}
	}
}

function setDateMode(mode, date) {
	datePickerMode.value = mode
	selectedOption.value = mode
	filterDate.value = date
}

function emitUpdate() {
	switch (datePickerMode.value) {
		case 'RANGE':
			emit('update', {
				sd: moment(filterDate.value.start).format('YYYY-MM-DD'),
				ed: moment(filterDate.value.end).format('YYYY-MM-DD'),
				duration: '',
			})
			break

		case 'AFTER':
			emit('update', {
				sd: moment(filterDate.value).format('YYYY-MM-DD'),
				ed: '',
				duration: '',
			})
			break

		case 'BEFORE':
			emit('update', {
				ed: moment(filterDate.value).format('YYYY-MM-DD'),
				sd: '',
				duration: '',
			})
			break

		case 'ANY':
			emit('update', { sd: '', ed: '', duration: '' })
			break

		case 'ON':
			emit('update', {
				sd: moment(filterDate.value).format('YYYY-MM-DD'),
				ed: moment(filterDate.value).format('YYYY-MM-DD'),
				duration: '',
			})
			break

		default:
			emit('update', { duration: selectedOption.value, sd: '', ed: '' })
			break
	}
}

function determineState() {
	resetOptions()
	datePickerMode.value = null
	selectedOption.value = null
	if (props.relativeDate) {
		filterDate.value = null
		selectedOption.value = props.relativeDate
	} else if (isRange.value) {
		setDateMode('RANGE', { start: props.startDate, end: props.endDate })
	} else if (isAfter.value) {
		setDateMode('AFTER', props.startDate)
	} else if (isBefore.value) {
		setDateMode('BEFORE', props.endDate)
	} else if (isSameDay.value) {
		setDateMode('ON', props.startDate)
	}
	updateOptions()
}

function dropdownOpen(open) {
	emit('open', open)
	isDropdownOpen.value = open
}

onMounted(() => {
	resetOptions()
	determineState()
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.date-filter-container {
	&.active {
		:deep(.dropdown-container) {
			.dropdown-selected {
				@include fontFamily(800);
				border-color: var(--blue-300);
				background: var(--blue-300);
				color: var(--neutral-0);
			}
			.dropdown-selected-label {
				color: var(--neutral-0);
			}
		}
	}

	:deep(.dropdown-container) {
		.dropdown-selected {
			background: var(--neutral-0);
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.date-filter-options {
	$height: 355px;
	margin-top: 10px !important;
	width: 230px !important;
	&.open {
		max-height: $height !important;
	}
	&.scrollable {
		.scroller {
			max-height: calc(#{$height} - 5px) !important;
		}
	}
	.dropdown-option-content {
		font-size: 14px;
		font-family: 'Avenir-Book';
		.checkmark-icon {
			font-size: 14px;
			color: var(--blue-300);
			margin-left: auto;
		}
		.selected-option {
			display: flex;
			justify-content: space-between;
			font-family: 'Avenir-Heavy';
			color: var(--blue-300);
		}
	}

	.dropdown-options {
		padding: 5px 10px 5px 10px !important;
		overflow: hidden !important;
		.dropdown-option {
			width: 100%;
			&.no-hover {
				padding: 0 !important;
			}
		}
	}
}

@include overrideScheme('dark') {
	.date-filter-options {
		background: var(--neutral-200) !important;
	}
	.date-filter-container {
		&.active {
			.dropdown-selected-label {
				color: white !important;
			}
			.pronto-icon.chevron {
				color: white !important;
			}
			.dropdown-container {
				.dropdown-selected {
					background: var(--blue-300) !important;
					.search-filter-placeholder {
						color: white !important;
					}
				}
			}
		}
	}
}
</style>
